// Smoke-test orchestrator (DEV/TEST APP ONLY — excluded from hardened builds
// via the @dev-only alias). Runs scenario modules sequentially against the
// LIVE dev Firestore with the real signed-in user, so results reflect the
// exact auth + security-rules path production users hit.

import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/store/authStore'
import { useSchoolStore } from '@/store/schoolStore'
import { getCurrentSchoolId } from '@/composables/useSchoolContext'
import { normalizeUserGovernance } from '@/utils/userGovernance'
import { devLog, devError } from '@/utils/environment'
import { isSmokeRunnerAvailable } from './availability.js'
import { loadSmokeAuthInspection } from './authContext.js'
import { scenarioStatusFromSteps, summarizeSmokeResults } from './stepStatus.js'
import { scenarios } from './scenarios/index.js'

export { scenarios }

function resolveActiveSchoolId() {
  try {
    const schoolStore = useSchoolStore()
    if (schoolStore.currentSchoolId) return schoolStore.currentSchoolId
  } catch {
    // Pinia not active
  }
  return null
}

/**
 * Wraps one scenario step: timing, pass/fail/skip/blocked capture, console echo.
 * Scenarios call `await step('name', async () => {...detail...})` — a thrown
 * error marks the step failed and aborts the rest of the scenario, but never
 * the whole run. Use `step.skip` / `step.block` for non-fail outcomes.
 */
function makeStepRecorder(scenarioId, results, onProgress, role = null) {
  function attach(record) {
    if (role) record.role = role
    results.push(record)
    onProgress?.(results)
    return record
  }

  async function step(name, fn) {
    const started = performance.now()
    const record = attach({ scenario: scenarioId, step: name, status: 'running' })
    try {
      const detail = await fn()
      record.status = 'pass'
      if (detail) record.detail = detail
    } catch (err) {
      record.status = 'fail'
      record.error = err?.message || String(err)
      record.errorCode = err?.code
      devError(`❌ [smoke:${scenarioId}] ${name}:`, err)
      throw err
    } finally {
      record.durationMs = Math.round(performance.now() - started)
      onProgress?.(results)
      if (record.status === 'pass') {
        devLog(`✅ [smoke:${scenarioId}] ${name} (${record.durationMs}ms)`)
      }
    }
  }

  step.skip = (name, detail) => {
    attach({ scenario: scenarioId, step: name, status: 'skip', detail, durationMs: 0 })
    devLog(`↷ [smoke:${scenarioId}] ${name}: ${detail}`)
  }

  step.block = (name, detail) => {
    attach({ scenario: scenarioId, step: name, status: 'blocked', detail, durationMs: 0 })
    devLog(`⚠ [smoke:${scenarioId}] ${name}: ${detail}`)
  }

  return step
}

/**
 * Run scenario modules against an already-built ctx. Used by a single-role
 * run and by the complete-check sweep (same JWT path, different role each
 * pass). A thrown step aborts that scenario only.
 */
export async function executeSmokeScenarios({
  scenarioIds,
  ctx,
  results,
  onProgress,
  role = null,
  scenarioStatusOut = null
}) {
  for (const id of scenarioIds) {
    const scenario = scenarios.find((s) => s.id === id)
    if (!scenario) continue
    const step = makeStepRecorder(id, results, onProgress, role)
    ctx.completedOnce ||= new Set()
    if (scenario.oncePerSweep && ctx.completedOnce.has(id)) {
      step.skip(
        'already checked in this sweep',
        'This scenario runs its own isolated workflow and district profiles once per report'
      )
      if (scenarioStatusOut) scenarioStatusOut[id] = 'skip'
      continue
    }
    if (scenario.oncePerSweep) ctx.completedOnce.add(id)
    try {
      await scenario.run(ctx, step)
    } catch {
      // Step recorder already captured the failure; keep going.
    }
    const steps = results.filter((r) => r.scenario === id && (role == null || r.role === role))
    const status = scenarioStatusFromSteps(steps)
    if (scenarioStatusOut) scenarioStatusOut[id] = status
    if (ctx.authInvalid) {
      step.block(
        'remaining scenarios',
        'Starting JWT role could not be restored. Stop and restore your demo role before continuing.'
      )
      if (scenarioStatusOut)
        scenarioStatusOut[id] = scenarioStatusFromSteps(
          results.filter((r) => r.scenario === id && (role == null || r.role === role))
        )
      break
    }
  }
}

/**
 * Run the given scenario ids in order. Returns a full report object suitable
 * for display and JSON export. Throws only for preflight (no user, spoofed
 * client role, runner unavailable). Step failures land in the report.
 *
 * @param {string[]} scenarioIds
 * @param {{ onProgress?: Function }} opts
 */
export async function runSmokeTest(scenarioIds, { onProgress, onReport } = {}) {
  if (!isSmokeRunnerAvailable()) {
    throw new Error('Smoke runner is not available in this build/project.')
  }

  const authStore = useAuthStore()
  const loaded = await loadSmokeAuthInspection(authStore.currentUser)
  if (!loaded.signedIn || !loaded.user) {
    throw new Error('No signed-in user — sign in before running the smoke test.')
  }
  if (loaded.inspection.blocked) {
    throw new Error(loaded.inspection.reasons.join(' '))
  }

  const user = loaded.user
  const runId = `smoke-${new Date().toISOString().replace(/[:.]/g, '-')}`
  const schoolId = resolveActiveSchoolId() || (await getCurrentSchoolId().catch(() => null))

  let userGovernance = normalizeUserGovernance({})
  try {
    const settingsSnap = await getDoc(doc(db, 'app_settings', 'global'))
    if (settingsSnap.exists()) {
      userGovernance = normalizeUserGovernance(settingsSnap.data()?.userGovernance)
    }
  } catch {
    // Probes that need governance still run with defaults.
  }

  const report = {
    runId,
    startedAt: new Date().toISOString(),
    finishedAt: null,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    user: {
      uid: user.uid,
      email: user.email,
      role: loaded.inspection.jwtRole,
      storeRole: loaded.inspection.storeRole
    },
    schoolId,
    results: [],
    summary: { pass: 0, fail: 0, skip: 0, blocked: 0, scenarios: {} }
  }

  const ctx = {
    user,
    runId,
    schoolId,
    roleClaim: loaded.inspection.jwtRole,
    tokenSchoolIds: loaded.tokenSchoolIds,
    userGovernance
  }

  onReport?.(report)
  devLog(`🤖 === SMOKE TEST RUN ${runId} ===`)
  await executeSmokeScenarios({
    scenarioIds,
    ctx,
    results: report.results,
    onProgress: (results) => {
      onProgress?.(results)
      onReport?.(report)
    },
    scenarioStatusOut: report.summary.scenarios
  })

  report.finishedAt = new Date().toISOString()
  Object.assign(report.summary, summarizeSmokeResults(report.results), {
    scenarios: report.summary.scenarios
  })
  devLog(
    `🤖 === SMOKE TEST DONE: ${report.summary.pass} pass, ${report.summary.fail} fail, ${report.summary.skip} skip, ${report.summary.blocked} blocked ===`,
    report
  )
  onReport?.(report)
  return report
}
